import { Button } from "../ui/button";
import { ArrowUp, ArrowDown, Edit2, Trash2, ExternalLink } from "lucide-react";

export interface SocialLink {
  name: string;
  url: string;
  icon: string;
  color: string;
  sort_order: number;
}

interface SocialRowActionsProps {
  social: SocialLink;
  idx: number;
  totalCount: number;
  onEdit: (social: SocialLink) => void;
  onDelete: (name: string) => void;
  onMove: (index: number, direction: "up" | "down") => void;
}

function SocialRowActions({
  social,
  idx,
  totalCount,
  onEdit,
  onDelete,
  onMove,
}: SocialRowActionsProps) {
  return (
    <>
      <td className="p-4 text-center">
        <div className="flex items-center justify-center gap-1">
          <Button
            size="icon"
            variant="ghost"
            onClick={() => onMove(idx, "up")}
            disabled={idx === 0}
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
          >
            <ArrowUp className="h-4.5 w-4.5" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            onClick={() => onMove(idx, "down")}
            disabled={idx === totalCount - 1}
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
          >
            <ArrowDown className="h-4.5 w-4.5" />
          </Button>
        </div>
      </td>
      <td className="p-4 text-center">
        <div className="flex items-center justify-center gap-2">
          <Button
            size="icon"
            variant="outline"
            onClick={() => onEdit(social)}
            className="h-8 w-8 border-border/60 hover:bg-brand-primary/10 hover:text-brand-primary text-muted-foreground"
          >
            <Edit2 className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="outline"
            onClick={() => onDelete(social.name)}
            className="h-8 w-8 border-border/60 hover:bg-destructive/10 hover:text-destructive text-muted-foreground"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </td>
    </>
  );
}

interface SocialRowProps {
  social: SocialLink;
  idx: number;
  totalCount: number;
  onEdit: (social: SocialLink) => void;
  onDelete: (name: string) => void;
  onMove: (index: number, direction: "up" | "down") => void;
}

function SocialRow({
  social,
  idx,
  totalCount,
  onEdit,
  onDelete,
  onMove,
}: SocialRowProps) {
  return (
    <tr className="hover:bg-muted/10 transition-colors">
      <td className="p-4 text-center font-mono font-bold text-muted-foreground">
        {idx + 1}
      </td>
      <td className="p-4 max-w-xs">
        <p className="font-semibold text-foreground leading-snug">
          {social.name}
        </p>
      </td>
      <td className="p-4 w-24">
        <span className="inline-flex items-center text-[10px] uppercase font-bold text-brand-secondary bg-brand-secondary/5 px-2 py-0.5 rounded border border-brand-secondary/10">
          {social.icon}
        </span>
      </td>
      <td className="p-4 max-w-xs">
        <a
          href={social.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center text-xs text-brand-primary hover:underline font-mono truncate max-w-[200px]"
        >
          <span className="truncate max-w-[150px]">{social.url}</span>
          <ExternalLink className="h-3 w-3 ml-1 shrink-0" />
        </a>
      </td>
      <td className="p-4 w-36">
        <div className="flex items-center gap-2">
          <div
            className="w-3.5 h-3.5 rounded-full border border-white/10 shadow-sm shrink-0"
            style={{ backgroundColor: social.color || "#6b7280" }}
          />
          <span className="font-mono text-xs text-muted-foreground">{social.color || "#6b7280"}</span>
        </div>
      </td>
      <SocialRowActions
        social={social}
        idx={idx}
        totalCount={totalCount}
        onEdit={onEdit}
        onDelete={onDelete}
        onMove={onMove}
      />
    </tr>
  );
}

interface SocialsListProps {
  sortedSocials: SocialLink[];
  handleMove: (index: number, direction: "up" | "down") => void;
  handleEdit: (social: SocialLink) => void;
  handleDelete: (name: string) => void;
}

export function SocialsList({
  sortedSocials,
  handleMove,
  handleEdit,
  handleDelete,
}: SocialsListProps) {
  if (sortedSocials.length === 0) {
    return (
      <div className="text-center py-12 border border-dashed border-border/60 rounded-2xl bg-card/40">
        <p className="text-sm font-semibold text-muted-foreground">
          No social links configured. Click the button above to add one.
        </p>
      </div>
    );
  }

  return (
    <div className="border border-border/60 bg-card rounded-2xl overflow-hidden shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse font-sans">
          <thead>
            <tr className="bg-muted/40 border-b border-border/60 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              <th className="p-4 w-12 text-center">#</th>
              <th className="p-4">Platform</th>
              <th className="p-4 w-24">Icon Tag</th>
              <th className="p-4">URL</th>
              <th className="p-4 w-36">Theme Color</th>
              <th className="p-4 w-28 text-center">Order</th>
              <th className="p-4 w-36 text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/40 text-sm">
            {sortedSocials.map((social, idx) => (
              <SocialRow
                key={social.name}
                social={social}
                idx={idx}
                totalCount={sortedSocials.length}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onMove={handleMove}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default SocialsList;

